import { z } from 'zod';
/**
 * "Sell a product" on the therapist tablet (owner 15.10.2026). Stock, prices and the sale itself
 * live in the studio's accounting system (uchet.360face.pt); Hub only shows the shelf, builds the
 * basket and forwards the sale. Money in Hub is integer cents; the accounting API speaks decimal
 * strings ("60.00") — converted here without floating point.
 */
/** Payment methods a product sale can use (no gift certificate). */
export const SHOP_PAYMENT_METHODS = ['card_terminal', 'mbway', 'cash', 'bank_transfer'];
/** "60.00" / "60" / "60.5" → 6000 / 6000 / 6050. Anything else → undefined. */
export function decimalToMinor(raw) {
    if (raw === null || raw === undefined)
        return undefined;
    const s = String(raw).trim();
    const m = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(s);
    if (!m)
        return undefined;
    return Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
}
/** 6000 → "60.00". */
export function minorToDecimal(minor) {
    if (!Number.isSafeInteger(minor) || minor < 0)
        throw new RangeError('minor');
    return `${Math.floor(minor / 100)}.${String(minor % 100).padStart(2, '0')}`;
}
/** "2.00" % → 200 basis points (hundredths of a percent). */
export function percentToBp(raw) {
    return decimalToMinor(raw);
}
/**
 * Price of one unit with the payment fee, as the accounting system computes it:
 * round(price × (1 + fee/100)) to the cent, half up — 60.00 € + 2 % = 61.20 €.
 */
export function unitWithFeeMinor(priceMinor, feeBp) {
    if (!Number.isSafeInteger(priceMinor) || priceMinor < 0)
        throw new RangeError('priceMinor');
    if (!Number.isSafeInteger(feeBp) || feeBp < 0)
        throw new RangeError('feeBp');
    return priceMinor + Math.floor((priceMinor * feeBp + 5000) / 10000);
}
/** Total to collect: per unit with fee (when the client pays it) × quantity. */
export function shopTotalMinor(lines, fee) {
    return lines.reduce((sum, l) => sum + (fee.clientPaysFee ? unitWithFeeMinor(l.priceMinor, fee.feeBp) : l.priceMinor) * l.qty, 0);
}
/** Lowest price allowed without the administrator for a list price and a limit in basis points. */
export function minAllowedPriceMinor(listPriceMinor, maxDiscountBp) {
    return listPriceMinor - Math.floor((listPriceMinor * maxDiscountBp) / 10000);
}
export const ShopSaleRequestSchema = z.object({
    staffId: z.string().regex(/^[a-z0-9-]{1,40}$/),
    method: z.enum(SHOP_PAYMENT_METHODS),
    /** Required when any price is below the list price (discount): why / client's name. */
    reason: z.string().trim().max(200).optional(),
    /** Unique per sale (created when the confirmation opens): a repeat returns the same sale. */
    reference: z.string().uuid(),
    items: z
        .array(z.object({
        productId: z.number().int().positive(),
        qty: z.number().int().min(1).max(20),
        /** Price per unit after a discount; omitted = list price. */
        priceMinor: z.number().int().min(1).max(10_000_000).optional(),
    }))
        .min(1)
        .max(20),
});
//# sourceMappingURL=shop.js.map